/**
 * 鉛直断面ビュー（横軸＝断面に沿った水平距離 / 縦軸＝高度）。
 *
 * 3D表示もカメラを寝かせれば高さの当たりは付く（`MAX_PITCH`）。ただし透視投影
 * なので遠近が付き、「NOTAMの天井まであと何ft か」を数値として読むことはできない。
 * この画面は地図の中心を通る鉛直な平面で空間を切り、高度を縦軸の目盛りに
 * 対応させて描くので、余裕をそのまま読める。3Dと役割を分けて両方を残している。
 *
 * ★ 縦軸＝高度・横軸＝水平距離。ウィンドプロファイラ（横軸＝時間）とは
 *   横軸の意味が違うので、目盛りのラベル（km）で必ず区別できるようにする。
 *
 * ★ 断面は「面」ではなく厚みを持った「板」として扱う。板の厚み（奥行き）の
 *   中に入った点だけを、板の中心面へ投影して描く。奥行きが大きいほど多くの
 *   機体が写るが、その分だけ横位置の誤差も増えるので、面から離れた点ほど
 *   薄く描いて区別できるようにする。
 *
 * DOM を触らない純粋関数にしてある（ui/wind-profile.ts と同じ流儀）。
 */

import {
  AIRSPACE_COLOR,
  colorForAltitude,
  colorForTerminal,
  NOTAM_COLOR,
  type AltitudeStop,
} from '../map/layers.ts';
import { feetToMeters, metersToFeet } from '../units.ts';

/**
 * 宣言ゴールの色。地図の3Dゴール球（gv-goal-3d-ball）と同じ値にしてある。
 * 同じゴールが画面ごとに違う色で出ると対応関係が読めなくなるので、
 * あちらを変えたらここも変えること。
 */
const GOAL_COLOR = '#4aa8ff';
const GOAL_SELECTED_COLOR = '#ff3b30';

/** 緯度1度あたりの距離 [m]。局所的な平面近似にのみ使う */
const M_PER_DEG_LAT = 110574;
/** 経度1度あたりの距離 [m]（赤道上）。実際には cos(緯度) を掛ける */
const M_PER_DEG_LON_EQUATOR = 111320;

/**
 * 断面の定義。中心（地図の中心）を通り、`bearingDeg` の方位へ伸びる鉛直面。
 * 断面座標系は次の2軸を持つ:
 *   along  … 断面に沿った水平距離 [m]。中心が0、bearingDeg の向きが正
 *   across … 断面からの距離 [m]。中心面が0、bearingDeg を向いたときの右手が正
 */
export interface SectionGeometry {
  centerLat: number;
  centerLon: number;
  /** 断面の方位 [deg]（真方位・0=北）。鉛直軸まわりの回転にあたる */
  bearingDeg: number;
  /** 断面の長さの半分 [m]。表示範囲は along ∈ [-halfLengthM, +halfLengthM] */
  halfLengthM: number;
  /** 断面の厚み（奥行き）の半分 [m]。|across| がこれ以下の点だけを描く */
  depthM: number;
}

/**
 * 緯度経度 → 断面座標。
 *
 * 会場スケール（数十km）の局所平面近似で足りるので、正確な測地線計算は
 * 行わない（UTM変換を通すより軽く、断面の見た目には影響しない）。
 */
export function projectToSection(
  lat: number,
  lon: number,
  geom: SectionGeometry,
): { along: number; across: number } {
  const latRad = (geom.centerLat * Math.PI) / 180;
  const east = (lon - geom.centerLon) * M_PER_DEG_LON_EQUATOR * Math.cos(latRad);
  const north = (lat - geom.centerLat) * M_PER_DEG_LAT;
  const b = (geom.bearingDeg * Math.PI) / 180;
  return {
    along: east * Math.sin(b) + north * Math.cos(b),
    across: east * Math.cos(b) - north * Math.sin(b),
  };
}

/**
 * 断面座標 → 緯度経度（projectToSection の逆変換）。
 * 断面線を地図上に描くために使う。
 *
 * 変換行列 [[sin b, cos b], [cos b, -sin b]] は対称かつ自身が逆行列なので、
 * 順変換とまったく同じ式で戻せる。
 */
export function sectionToLatLon(
  along: number,
  across: number,
  geom: SectionGeometry,
): { lat: number; lon: number } {
  const latRad = (geom.centerLat * Math.PI) / 180;
  const b = (geom.bearingDeg * Math.PI) / 180;
  const east = along * Math.sin(b) + across * Math.cos(b);
  const north = along * Math.cos(b) - across * Math.sin(b);
  return {
    lat: geom.centerLat + north / M_PER_DEG_LAT,
    lon: geom.centerLon + east / (M_PER_DEG_LON_EQUATOR * Math.cos(latRad)),
  };
}

/**
 * 多角形が断面の中心線と交わる区間（along の範囲）を求める。
 *
 * 断面の中心線は across = 0 の直線なので、多角形の各辺が across = 0 を
 * またぐ点の along を集めて昇順に並べ、偶奇規則で2つずつ組にすれば
 * 「多角形の内側にある区間」になる。凹多角形でも正しく複数区間を返す。
 *
 * NOTAM高度制限区域は「その区間だけ天井がある」という形で描きたいので、
 * ゾーンの平面形状を断面上の水平な線分に落とすこの計算が要になる。
 *
 * ★ 断面がゾーンの**内部を通る**ときだけ区間を返す。境界に接するだけ
 *   （断面がゾーンの辺とちょうど重なる）場合は返さない。渡良瀬BluePZの
 *   ように隣接ゾーンが辺を共有していると、断面をその共有辺にぴったり
 *   合わせたときに片側のゾーンだけが描かれることになるが、実データで
 *   そこまで一致させられることは無く、隣接ゾーンのもう一方の天井は
 *   必ず描かれるため実用上の問題にはならない。
 */
export function polygonSpansAlongSection(
  points: ReadonlyArray<{ lat: number; lon: number }>,
  geom: SectionGeometry,
): Array<[number, number]> {
  if (points.length < 3) return [];
  const projected = points.map((p) => projectToSection(p.lat, p.lon, geom));

  const crossings: number[] = [];
  for (let i = 0, j = projected.length - 1; i < projected.length; j = i++) {
    const a = projected[j];
    const b = projected[i];
    // 辺が across=0 をまたぐか（片側の端点が0ちょうどの場合の二重計上を
    // 避けるため、判定は左閉右開にそろえる）
    if (a.across > 0 !== b.across > 0) {
      const ratio = (0 - a.across) / (b.across - a.across);
      crossings.push(a.along + (b.along - a.along) * ratio);
    }
  }

  crossings.sort((x, y) => x - y);
  const spans: Array<[number, number]> = [];
  for (let i = 0; i + 1 < crossings.length; i += 2) {
    spans.push([crossings[i], crossings[i + 1]]);
  }
  return spans;
}

/**
 * 断面の位置を地図に描くための GeoJSON。
 * 中心線（LineString）と、奥行きの帯（Polygon）の2つを返す。
 *
 * ★ layers.ts ではなくこちらに置いてある。cross-section.ts が layers.ts の
 *   配色ヘルパーを使っているため、逆向きの import を足すと循環参照になる。
 */
export function sectionToGeoJson(geom: SectionGeometry): GeoJSON.FeatureCollection {
  const half = geom.halfLengthM;
  const d = geom.depthM;
  const at = (along: number, across: number): [number, number] => {
    const p = sectionToLatLon(along, across, geom);
    return [p.lon, p.lat];
  };

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { kind: 'section-band' },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [at(-half, -d), at(half, -d), at(half, d), at(-half, d), at(-half, -d)],
          ],
        },
      },
      {
        type: 'Feature',
        properties: { kind: 'section-line' },
        geometry: { type: 'LineString', coordinates: [at(-half, 0), at(half, 0)] },
      },
    ],
  };
}

/** 断面に描く1本の航跡（機体1つぶん） */
export interface CrossSectionTrack {
  terminalId: string;
  label: string;
  points: Array<{ lat: number; lon: number; alt: number; t: number }>;
  /** 最新位置（あれば強調して描く）。過去のIGCフライトには無い */
  latest?: { lat: number; lon: number; alt: number; t: number } | null;
}

/** 断面に描く天井（NOTAM高度制限区域のゾーン1つぶん） */
export interface CrossSectionCeiling {
  label: string;
  /** 実効天井 [ft MSL]（override考慮後の値を呼び出し側で決めて渡す） */
  ceilingFt: number;
  points: Array<{ lat: number; lon: number }>;
}

/**
 * 断面に描く空域（3DT の設定空域）。NOTAM の天井が「これより上に行けない」
 * 面なのに対し、こちらは「この中を飛びたい」箱なので、上下限で挟んだ矩形として
 * 描き分ける。
 */
export interface CrossSectionVolume {
  label: string;
  points: Array<{ lat: number; lon: number }>;
  /** 下限 [ft MSL]。null なら地面 */
  floorFt: number | null;
  /** 上限 [ft MSL]。null なら画面上端まで */
  ceilingFt: number | null;
}

/**
 * 風を断面の座標系へ分解する。
 *
 * `dirDeg` は「気球が流される方向」（gv-core の WindSample の規約）。
 * 風速ベクトルを (東, 北) で表すと (speed·sin dir, speed·cos dir) なので、
 * 断面の基底（projectToSection と同じ）に射影すると次のようになる:
 *
 *   along  = speed·cos(dir − bearing)  … 断面に沿った成分（画面の左右）
 *   across = speed·sin(dir − bearing)  … 断面を貫く成分（画面の手前／奥）
 *
 * ★ この分解が断面図の要。競技では断面をゴール方向に向けて切ることが多く、
 *   along が「ゴールへどれだけ進むか」、across が「どれだけ横へ流されるか」に
 *   そのまま対応する。
 */
export function sectionWindComponents(
  dirDeg: number,
  speed: number,
  bearingDeg: number,
): { along: number; across: number } {
  const rel = ((dirDeg - bearingDeg) * Math.PI) / 180;
  return { along: speed * Math.cos(rel), across: speed * Math.sin(rel) };
}

/** 断面に重ねる風サンプル1点 */
export interface CrossSectionWindSample {
  lat: number;
  lon: number;
  /** 高度 [m] */
  alt: number;
  /** 風向 [deg]（気球が流される方向） */
  dir: number;
  /** 風速 [m/s] */
  speed: number;
}

/** 断面に描く空中ゴール（高度を持つ宣言ゴール） */
export interface CrossSectionAerialGoal {
  label: string;
  lat: number;
  lon: number;
  altFt: number;
  /** 判定容積の水平半径 [m]。無ければ点として描く */
  radiusM?: number | null;
  /** 判定容積の上下許容差 [ft]。無ければ点として描く */
  altToleranceFt?: number | null;
  selected: boolean;
}

export interface CrossSectionInput {
  geom: SectionGeometry;
  tracks: CrossSectionTrack[];
  ceilings: CrossSectionCeiling[];
  /** 3DT の設定空域（省略可） */
  volumes?: CrossSectionVolume[];
  /** 空中ゴール（省略可） */
  aerialGoals?: CrossSectionAerialGoal[];
  /** 重ねる風（省略可）。空なら描かない */
  wind?: CrossSectionWindSample[];
  /** 高度の目盛りと配色。地図・ウィンドプロファイラと同じものを渡す */
  stops: AltitudeStop[];
  width: number;
  height: number;
}

/** 描画領域の余白。左は高度ラベル、下は距離ラベルのぶん広く取る */
const PAD = { top: 16, right: 16, bottom: 34, left: 54 };
/** 現在位置の丸の半径 [px] */
const LATEST_RADIUS = 5;

/**
 * 風の色。航跡（機体色）・NOTAM（青）・3DT空域（緑）・ゴール（水色/赤）の
 * どれとも競合しない、色味を持たない明るいグレーにする。
 * 断面図の風は「地物」ではなく場の情報なので、彩度を持たせない方が読みやすい。
 */
const WIND_COLOR = '#dfe9f5';
/** 風の矢印の最大の長さ [px]。隣の格子へはみ出しすぎないよう頭打ちにする */
const WIND_ARROW_MAX_PX = 30;
/** この風速 [m/s] で矢印が最大長になる */
const WIND_FULL_SCALE_MS = 10;
/** 風を間引く格子。断面に沿った距離 [m] と高度 [m] */
const WIND_BIN_ALONG_M = 700;
const WIND_BIN_ALT_M = 100;
/** 面直交成分の記号（⊙/⊗）の最大半径 [px] */
const WIND_OOP_MAX_R = 6;

/**
 * 風を格子で間引き、断面座標での平均ベクトルにする。
 *
 * 同じ場所・同じ高度に何十点も溜まると矢印が重なって読めなくなるため、
 * (断面に沿った距離 × 高度) の格子ごとにベクトル平均を取る。
 * 角度ではなくベクトルで平均すること（0°と350°の平均が175°になる事故を防ぐ）。
 */
export function binSectionWind(
  samples: readonly CrossSectionWindSample[],
  geom: SectionGeometry,
  halfLengthM: number,
): Array<{ along: number; alt: number; vAlong: number; vAcross: number; n: number }> {
  const bins = new Map<
    string,
    { along: number; alt: number; vAlong: number; vAcross: number; n: number }
  >();

  for (const s of samples) {
    const { along, across } = projectToSection(s.lat, s.lon, geom);
    if (Math.abs(across) > geom.depthM || along < -halfLengthM || along > halfLengthM) continue;
    const c = sectionWindComponents(s.dir, s.speed, geom.bearingDeg);
    const alongBin = Math.round(along / WIND_BIN_ALONG_M);
    const altBin = Math.round(s.alt / WIND_BIN_ALT_M);
    const key = `${alongBin}:${altBin}`;
    const cur = bins.get(key);
    if (cur) {
      cur.along += along;
      cur.alt += s.alt;
      cur.vAlong += c.along;
      cur.vAcross += c.across;
      cur.n++;
    } else {
      bins.set(key, { along, alt: s.alt, vAlong: c.along, vAcross: c.across, n: 1 });
    }
  }

  return [...bins.values()].map((b) => ({
    along: b.along / b.n,
    alt: b.alt / b.n,
    vAlong: b.vAlong / b.n,
    vAcross: b.vAcross / b.n,
    n: b.n,
  }));
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * 表示する高度の上限 [m]。
 * 実データ・天井の両方が色分けの最上段を超えうるので、3つとも見て決める。
 * 天井を必ず画面内に入れないと「達しているかどうか」が読めないため。
 */
function topAltitudeM(input: CrossSectionInput): number {
  let max = input.stops.length > 0 ? input.stops[input.stops.length - 1].m : 0;
  for (const t of input.tracks) {
    for (const p of t.points) if (p.alt > max) max = p.alt;
  }
  for (const c of input.ceilings) {
    const m = feetToMeters(c.ceilingFt);
    if (m > max) max = m;
  }
  for (const v of input.volumes ?? []) {
    if (v.ceilingFt == null) continue;
    const m = feetToMeters(v.ceilingFt);
    if (m > max) max = m;
  }
  for (const g of input.aerialGoals ?? []) {
    const m = feetToMeters(g.altFt + (g.altToleranceFt ?? 0));
    if (m > max) max = m;
  }
  return max > 0 ? max * 1.08 : 100;
}

/** 断面からの距離に応じた不透明度。面上=1、板の縁=0.35 */
function opacityForAcross(across: number, depthM: number): number {
  if (depthM <= 0) return 1;
  const ratio = Math.min(1, Math.abs(across) / depthM);
  return 1 - ratio * 0.65;
}

/**
 * 方位を「N / NE / E …」の16方位ではなく、断面の向きが一目で分かる
 * 「135° → 315°」形式で表す（断面は両方向に伸びるため）。
 */
export function sectionBearingLabel(bearingDeg: number): string {
  const a = ((bearingDeg % 360) + 360) % 360;
  const b = (a + 180) % 360;
  return `${Math.round(b)}° ↔ ${Math.round(a)}°`;
}

export function buildCrossSectionSvg(input: CrossSectionInput): string {
  const { geom, tracks, ceilings, stops, width, height } = input;

  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = Math.max(1, height - PAD.top - PAD.bottom);
  const halfLen = Math.max(1, geom.halfLengthM);
  const topM = topAltitudeM(input);

  const xOf = (alongM: number) => PAD.left + ((alongM + halfLen) / (2 * halfLen)) * plotW;
  const yOf = (altM: number) => PAD.top + plotH - (altM / topM) * plotH;
  const f = (n: number) => n.toFixed(1);

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
      `width="100%" height="100%" role="img" aria-label="鉛直断面（横軸＝水平距離／縦軸＝高度）">`,
  );

  // --- 高度の目盛り（地図の凡例・ウィンドプロファイラと同じ段・同じ色） ---
  for (const stop of stops) {
    if (stop.m > topM) continue;
    const y = yOf(stop.m);
    parts.push(
      `<line x1="${PAD.left}" y1="${f(y)}" x2="${f(PAD.left + plotW)}" y2="${f(y)}" ` +
        `stroke="${stop.color}" stroke-opacity="0.22" stroke-width="1" />`,
    );
    parts.push(
      `<text x="${PAD.left - 6}" y="${f(y + 3.5)}" text-anchor="end" ` +
        `font-size="10" fill="currentColor" fill-opacity="0.65">${stop.ft}</text>`,
    );
  }
  parts.push(
    `<text x="4" y="${f(PAD.top - 5)}" font-size="9" fill="currentColor" fill-opacity="0.5">ft</text>`,
  );

  // --- 距離の目盛り（中心が0。断面の向きの正側が右） ---
  const tickCount = 4;
  for (let i = 0; i <= tickCount; i++) {
    const alongM = -halfLen + (2 * halfLen * i) / tickCount;
    const x = xOf(alongM);
    parts.push(
      `<line x1="${f(x)}" y1="${PAD.top}" x2="${f(x)}" y2="${f(PAD.top + plotH)}" ` +
        `stroke="currentColor" stroke-opacity="${i === tickCount / 2 ? '0.35' : '0.1'}" stroke-width="1" />`,
    );
    const anchor = i === 0 ? 'start' : i === tickCount ? 'end' : 'middle';
    const km = alongM / 1000;
    parts.push(
      `<text x="${f(x)}" y="${f(PAD.top + plotH + 15)}" text-anchor="${anchor}" ` +
        `font-size="10" fill="currentColor" fill-opacity="0.65">` +
        `${km === 0 ? '0' : `${km > 0 ? '+' : ''}${km.toFixed(1)}`}</text>`,
    );
  }
  parts.push(
    `<text x="${f(PAD.left + plotW)}" y="${f(PAD.top + plotH + 28)}" text-anchor="end" ` +
      `font-size="9" fill="currentColor" fill-opacity="0.5">km</text>`,
  );

  // --- NOTAM天井（この画面の主役）---
  // 天井「より上」を飛べないので、天井から上端までを塗って侵入不可の空間を示す。
  // 天井そのものは実線で描き、どの高度なのかをラベルで添える。
  for (const c of ceilings) {
    const ceilM = feetToMeters(c.ceilingFt);
    const yCeil = yOf(ceilM);
    for (const [fromM, toM] of polygonSpansAlongSection(c.points, geom)) {
      // 表示範囲の外にはみ出す部分は切り詰める（描画は範囲内だけでよい）
      const a = Math.max(fromM, -halfLen);
      const b = Math.min(toM, halfLen);
      if (b <= a) continue;
      const x1 = xOf(a);
      const x2 = xOf(b);
      parts.push(
        `<rect class="xs-ceiling-band" x="${f(x1)}" y="${f(PAD.top)}" ` +
          `width="${f(x2 - x1)}" height="${f(Math.max(0, yCeil - PAD.top))}" ` +
          `fill="${NOTAM_COLOR}" fill-opacity="0.16" />`,
      );
      parts.push(
        `<line class="xs-ceiling-line" x1="${f(x1)}" y1="${f(yCeil)}" x2="${f(x2)}" y2="${f(yCeil)}" ` +
          `stroke="${NOTAM_COLOR}" stroke-width="2" stroke-opacity="0.95" />`,
      );
      parts.push(
        `<text x="${f((x1 + x2) / 2)}" y="${f(yCeil - 5)}" text-anchor="middle" font-size="10" ` +
          `fill="${NOTAM_COLOR}">${escapeXml(c.label)} ${c.ceilingFt}ft</text>`,
      );
    }
  }

  // --- 3DT 設定空域 ---
  // NOTAMの天井（越えてはいけない面）と取り違えないよう、上下限で挟んだ
  // 「入りたい箱」として塗りと枠で描く。色も別（緑）にしてある。
  for (const v of input.volumes ?? []) {
    const yTop = v.ceilingFt == null ? PAD.top : yOf(feetToMeters(v.ceilingFt));
    const yBottom = v.floorFt == null ? PAD.top + plotH : yOf(feetToMeters(v.floorFt));
    for (const [fromM, toM] of polygonSpansAlongSection(v.points, geom)) {
      const a = Math.max(fromM, -halfLen);
      const b = Math.min(toM, halfLen);
      if (b <= a) continue;
      const x1 = xOf(a);
      const x2 = xOf(b);
      const h = Math.max(0, yBottom - yTop);
      parts.push(
        `<rect class="xs-volume" x="${f(x1)}" y="${f(yTop)}" width="${f(x2 - x1)}" ` +
          `height="${f(h)}" fill="${AIRSPACE_COLOR}" fill-opacity="0.14" ` +
          `stroke="${AIRSPACE_COLOR}" stroke-width="1.5" stroke-opacity="0.85" />`,
      );
      parts.push(
        `<text x="${f((x1 + x2) / 2)}" y="${f(yTop + 13)}" text-anchor="middle" font-size="10" ` +
          `fill="${AIRSPACE_COLOR}">${escapeXml(v.label)}</text>`,
      );
    }
  }

  // --- 風（航跡より先に描いて下に敷く）---
  // ★ 断面図の風は「方位の矢印」ではなく**断面座標への分解**で描く。
  //   横軸が既に地理的な向き（断面の方位）を持っているので、方位の矢印を
  //   重ねると「右向き＝断面の右」と読まれて必ず誤解される。
  //   面内成分（along）を水平の矢印、面直交成分（across）を ⊙/⊗ で表す
  //   ——気象の鉛直断面図と同じ作法。
  for (const w of binSectionWind(input.wind ?? [], geom, halfLen)) {
    const x = xOf(w.along);
    const y = yOf(w.alt);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    // 面内成分: 水平の矢印。長さ∝速さ、向き（左右）が断面に沿った流れ
    const len = Math.min(
      WIND_ARROW_MAX_PX,
      (Math.abs(w.vAlong) / WIND_FULL_SCALE_MS) * WIND_ARROW_MAX_PX,
    );
    if (len >= 2) {
      const sign = w.vAlong >= 0 ? 1 : -1;
      const tipX = x + sign * len;
      const head = Math.min(5, len * 0.45);
      parts.push(
        `<path class="xs-wind" d="M ${f(x)} ${f(y)} L ${f(tipX)} ${f(y)} ` +
          `M ${f(tipX)} ${f(y)} L ${f(tipX - sign * head)} ${f(y - head * 0.6)} ` +
          `L ${f(tipX - sign * head)} ${f(y + head * 0.6)} Z" ` +
          `fill="${WIND_COLOR}" stroke="${WIND_COLOR}" stroke-width="1.1" ` +
          `stroke-linecap="round" stroke-linejoin="round" fill-opacity="0.9" stroke-opacity="0.9" />`,
      );
    }

    // 面直交成分: ⊙ = 手前（見ている側）へ / ⊗ = 奥へ。
    // +across は断面を向いたときの右手側で、画面では手前にあたる
    // （断面の向き a と鉛直上向き u に対し a×u が across と一致するため）
    const oopR = Math.min(
      WIND_OOP_MAX_R,
      (Math.abs(w.vAcross) / WIND_FULL_SCALE_MS) * WIND_OOP_MAX_R,
    );
    if (oopR >= 2) {
      const toward = w.vAcross >= 0;
      parts.push(
        `<circle class="xs-wind-oop" cx="${f(x)}" cy="${f(y)}" r="${f(oopR)}" ` +
          `fill="none" stroke="${WIND_COLOR}" stroke-width="1" stroke-opacity="0.85" />`,
      );
      parts.push(
        toward
          ? `<circle cx="${f(x)}" cy="${f(y)}" r="${f(Math.max(1, oopR * 0.34))}" ` +
            `fill="${WIND_COLOR}" fill-opacity="0.9" />`
          : `<path d="M ${f(x - oopR * 0.7)} ${f(y - oopR * 0.7)} L ${f(x + oopR * 0.7)} ${f(y + oopR * 0.7)} ` +
            `M ${f(x + oopR * 0.7)} ${f(y - oopR * 0.7)} L ${f(x - oopR * 0.7)} ${f(y + oopR * 0.7)}" ` +
            `stroke="${WIND_COLOR}" stroke-width="1" stroke-opacity="0.85" />`,
      );
    }
  }

  // --- 航跡 ---
  // 板の厚みに入っている連続した区間ごとに1本のポリラインにする。
  // 途中で板から出た点でつないでしまうと、実際には通っていない線が
  // 断面上に現れてしまうため、区間を分けて描く。
  let drawnPoints = 0;
  for (const track of tracks) {
    const stroke = colorForTerminal(track.terminalId);
    let run: Array<{ x: number; y: number; across: number }> = [];

    const flushRun = (): void => {
      if (run.length >= 2) {
        const d = run.map((p, i) => `${i === 0 ? 'M' : 'L'} ${f(p.x)} ${f(p.y)}`).join(' ');
        const meanAcross = run.reduce((s, p) => s + Math.abs(p.across), 0) / run.length;
        parts.push(
          `<path class="xs-track" d="${d}" fill="none" stroke="${stroke}" stroke-width="1.6" ` +
            `stroke-opacity="${opacityForAcross(meanAcross, geom.depthM).toFixed(2)}" ` +
            `stroke-linecap="round" stroke-linejoin="round" />`,
        );
      }
      run = [];
    };

    for (const p of track.points) {
      const { along, across } = projectToSection(p.lat, p.lon, geom);
      if (Math.abs(across) > geom.depthM || along < -halfLen || along > halfLen) {
        flushRun();
        continue;
      }
      run.push({ x: xOf(along), y: yOf(p.alt), across });
      drawnPoints++;
    }
    flushRun();

    // 現在位置。高度色の丸＋機体色の輪郭（地図・ウィンドプロファイラと同じ使い分け）
    const latest = track.latest;
    if (!latest) continue;
    const { along, across } = projectToSection(latest.lat, latest.lon, geom);
    if (Math.abs(across) > geom.depthM || along < -halfLen || along > halfLen) continue;
    const x = xOf(along);
    const y = yOf(latest.alt);
    parts.push(
      `<circle class="xs-latest" cx="${f(x)}" cy="${f(y)}" r="${LATEST_RADIUS}" ` +
        `fill="${colorForAltitude(latest.alt, stops)}" stroke="${stroke}" stroke-width="2" ` +
        `data-terminal="${escapeXml(track.terminalId)}" data-label="${escapeXml(track.label)}" ` +
        `data-alt="${latest.alt}" data-along="${along.toFixed(1)}" data-across="${across.toFixed(1)}" />`,
    );
    parts.push(
      `<text x="${f(x + LATEST_RADIUS + 4)}" y="${f(y + 3.5)}" font-size="10" fill="${stroke}">` +
        `${escapeXml(track.label)} ${Math.round(metersToFeet(latest.alt))}ft</text>`,
    );
    drawnPoints++;
  }

  // --- 空中ゴール ---
  // 高度を持つ宣言ゴール。判定容積（半径・上下許容差）が分かっていれば箱として、
  // 分かっていなければ点として描く。★ 許容範囲は大会のタスクデータ側の値であり、
  // ここで既定値をでっち上げない（無ければ「点」として正直に描く）。
  for (const g of input.aerialGoals ?? []) {
    const { along, across } = projectToSection(g.lat, g.lon, geom);
    if (Math.abs(across) > geom.depthM || along < -halfLen || along > halfLen) continue;
    const x = xOf(along);
    const y = yOf(feetToMeters(g.altFt));
    const stroke = g.selected ? GOAL_SELECTED_COLOR : GOAL_COLOR;

    if (g.radiusM != null && g.altToleranceFt != null) {
      // 断面上の見かけの幅は、球ではなく円柱を切った幅（=半径）で決まる。
      // 断面が中心を外れていれば実際の切り口は狭くなるので、そのぶん詰める
      const halfWidthM = Math.sqrt(Math.max(0, g.radiusM ** 2 - across ** 2));
      const x1 = xOf(Math.max(along - halfWidthM, -halfLen));
      const x2 = xOf(Math.min(along + halfWidthM, halfLen));
      const yTop = yOf(feetToMeters(g.altFt + g.altToleranceFt));
      const yBottom = yOf(feetToMeters(g.altFt - g.altToleranceFt));
      parts.push(
        `<rect class="xs-aerial-goal" x="${f(x1)}" y="${f(yTop)}" width="${f(Math.max(1, x2 - x1))}" ` +
          `height="${f(Math.max(1, yBottom - yTop))}" fill="${stroke}" fill-opacity="0.18" ` +
          `stroke="${stroke}" stroke-width="1.5" />`,
      );
    }
    parts.push(
      `<circle class="xs-aerial-goal-point" cx="${f(x)}" cy="${f(y)}" r="4" ` +
        `fill="${stroke}" stroke="#ffffff" stroke-width="1" />`,
    );
    parts.push(
      `<text x="${f(x + 7)}" y="${f(y - 6)}" font-size="10" fill="${stroke}">` +
        `${escapeXml(g.label)} ${Math.round(g.altFt)}ft</text>`,
    );
  }

  // --- 断面の向き（左端＝逆方位 / 右端＝方位）---
  const backBearing = Math.round((((geom.bearingDeg % 360) + 360) % 360 + 180) % 360);
  const fwdBearing = Math.round(((geom.bearingDeg % 360) + 360) % 360);
  parts.push(
    `<text x="${PAD.left}" y="${f(PAD.top - 5)}" font-size="9" fill="currentColor" ` +
      `fill-opacity="0.6">← ${backBearing}°</text>`,
  );
  parts.push(
    `<text x="${f(PAD.left + plotW)}" y="${f(PAD.top - 5)}" text-anchor="end" font-size="9" ` +
      `fill="currentColor" fill-opacity="0.6">${fwdBearing}° →</text>`,
  );

  if (drawnPoints === 0) {
    parts.push(
      `<text x="${f(width / 2)}" y="${f(height / 2)}" text-anchor="middle" ` +
        `font-size="12" fill="currentColor" fill-opacity="0.55">` +
        `この断面の奥行きに入る航跡がありません</text>`,
    );
  }

  parts.push('</svg>');
  return parts.join('');
}
